import { toNumber } from './reportGridUtils'

/**
 * CC-50050 — the "sectioned summary" legacy-CSV shape.
 *
 * The Production & Origination report (specialized `cbRevenueAndCommissionsView`,
 * vladdemo reports 25 / 223, tenant-named "Shareholder Compensation for Production
 * and Originations") does not export a flat table. It exports a SECTIONED document
 * whose structure is carried positionally in a 15-column grid:
 *
 *   SECTION  "ADMIN"                                  <- attorney (parent group)
 *   SECTION    "A O Bailey Inc"                       <- client (child group)
 *   leaf         invoice detail / origination detail
 *   OPENER     "A O Bailey Inc" | Production   | $0.00      <- child summary block
 *   CONT                        | Origination  | $1,979.49
 *   CONT                        | Total        | $1,979.49
 *   …more child sections + blocks…
 *   OPENER   "Admin"            | Production   | $0.00      <- PARENT summary block
 *   CONT                        | Origination  | $1,979.49
 *   CONT                        | Total        | $1,979.49
 *   …
 *   OPENER   "Firm"             | Production   | $0.00      <- firm-level block
 *   OPENER   "Final Total"      | Production   | $725,931.06   <- REPORT footer
 *   CONT                        | Origination  | $1,971,193.61
 *   CONT                        | Expenses     | $99,075.24
 *   CONT                        | Grand Total  | $2,796,199.91
 *
 * A summary block is a run of rows whose only populated cells are in the TRAILING
 * three columns: an optional scope label (n-3, present on the opener only), a
 * measure label (n-2) and an amount (n-1).
 *
 * Why the generic 'header-rows' walk in CsvReportGrid gets this wrong — the two
 * facts this module exists to fix, both verified against the live 4,519-row payload:
 *
 *  1. It classifies total rows with `/\btotal\b/i` on the first non-empty cell. Of a
 *     block's measures only "Total" and "Grand Total" match, so "Production",
 *     "Origination" and "Expenses" fall through to the DATA path and render as
 *     near-empty detail rows. That is QA's "the Grand Total section is missing
 *     Production, Origination and Expenses".
 *  2. It looks for the block's owner in column 0, but the scope label sits at n-3
 *     and only on the opener. So the block has no recognisable owner (the subtotal
 *     renders as a bare "Total" you must scroll up to attribute) AND the scope leaks
 *     into the "Origination %" column — the sole source of all 690 non-numeric
 *     values in that column, which made sorting/filtering it nonsense.
 *
 * Classifying by SHAPE fixes both without widening that shared `/\btotal\b/i` regex,
 * which Time Entries, Timekeeper Analysis, Monthly Productivity and Fee Receipts all
 * depend on keeping narrow.
 *
 * GUARDRAIL: this module only ever RE-HOMES cells. Every amount is passed through
 * verbatim, in CB's order — nothing is summed, derived, reordered or dropped. This
 * is shareholder-compensation data.
 */

// Populated (non-blank) column indexes of a row, ascending.
const populatedIndexes = (row, headers) => {
  const out = []
  for (let i = 0; i < headers.length; i += 1) {
    if (String(row[headers[i]] ?? '').trim() !== '') out.push(i)
  }
  return out
}

/** A section label: column 0 alone carries a value. */
export const isSectionLabelRow = (row, headers) => {
  const p = populatedIndexes(row, headers)
  return p.length === 1 && p[0] === 0
}

/** A summary-block opener: exactly {scope, measure, amount} = the last three columns. */
export const isSummaryOpenerRow = (row, headers) => {
  const n = headers.length
  if (n < 3) return false
  const p = populatedIndexes(row, headers)
  return p.length === 3 && p[0] === n - 3 && p[1] === n - 2 && p[2] === n - 1
}

/** A summary-block continuation: exactly {measure, amount} = the last two columns. */
export const isSummaryContinuationRow = (row, headers) => {
  const n = headers.length
  if (n < 3) return false
  const p = populatedIndexes(row, headers)
  return p.length === 2 && p[0] === n - 2 && p[1] === n - 1
}

/**
 * True when the CSV is the sectioned-summary shape. Deliberately conservative — a
 * report must show BOTH section labels and real multi-line summary blocks before we
 * take this path, so ordinary flat and 'header-rows' CSVs are never diverted here.
 */
export const detectSectionedSummaryCsv = (headers, rows) => {
  if (!headers || headers.length < 3 || !rows || rows.length < 3) return false
  let sections = 0
  let openers = 0
  let continuations = 0
  for (const r of rows) {
    if (isSectionLabelRow(r, headers)) sections += 1
    else if (isSummaryOpenerRow(r, headers)) openers += 1
    else if (isSummaryContinuationRow(r, headers)) continuations += 1
  }
  return sections >= 2 && openers >= 2 && continuations >= 2
}

/**
 * Restructure a sectioned-summary CSV into ReportGroupedGrid's shape.
 *
 * Ownership is resolved from the data, not from position: a block's scope label is
 * matched (case-insensitively) against the open section stack, innermost outward.
 * Against the live payload this is fully deterministic — 689 of 690 blocks match the
 * innermost open section, and the single exception ("Final Total", with nothing
 * open) is the report footer. A block that matches nothing becomes the report
 * footer, so an unexpected scope degrades to a visible total rather than vanishing.
 *
 * Each measure becomes its own footer LINE, labelled "{scope} — {measure}" in the
 * first column so a subtotal always says whose it is, with the amount left in its
 * own column. Blocks therefore render as, e.g.:
 *
 *   Firm — Production                                              $0.00
 *   Firm — Origination                                      $1,447,096.89
 *   Firm — Total                                            $1,447,096.89
 *
 * @returns {{columns, reportData, reportFooterRows, reportFooterCells, recordCount}}
 */
export const buildSectionedSummaryReport = ({ headers, rows, columns, numericCols }) => {
  const n = headers.length
  const scopeIdx = n - 3
  const measureIdx = n - 2
  const amountIdx = n - 1
  const text = (row, i) => String(row[headers[i]] ?? '').trim()

  const mkCells = (row) =>
    headers.map((h, i) => ({
      columnId: i,
      displayValue: String(row[h] ?? ''),
      // dataValue only for numeric columns, so sort/SUM stay value-based.
      dataValue: numericCols?.has(h) ? toNumber(row[h] ?? '') : undefined,
    }))

  // One footer line: the composed label in column 0, the amount verbatim in its own
  // column. Only cells that carry something are emitted, matching how every other
  // footer in the grid is built.
  const mkFooterLine = (scope, measure, amount) => {
    const label = scope ? `${scope} — ${measure}` : measure
    const cells = []
    if (label.trim() !== '') cells.push({ columnId: 0, displayValue: label })
    if (String(amount).trim() !== '') cells.push({ columnId: amountIdx, displayValue: String(amount) })
    return cells
  }

  const groups = []
  const topLeaves = []
  const stack = []
  let leafCount = 0
  let pending = null
  let reportFooterRows = []

  const inner = () => stack[stack.length - 1] || null
  const openSection = (name) => {
    stack.push({ isGroup: true, groupHeaderContent: name, dataRows: [], groupFooterCells: [], groupFooterRows: [] })
  }
  const closeInnermost = (footerLines) => {
    const g = stack.pop()
    if (!g) return
    if (footerLines && footerLines.length) {
      g.groupFooterRows = footerLines
      // Keep the single-row field populated so existing consumers (the export
      // view's per-group `footer`, PDF "summary only") behave as before.
      g.groupFooterCells = footerLines[0]
    }
    const parent = inner()
    if (parent) parent.dataRows.push(g)
    else groups.push(g)
  }

  // Attach the accumulated block to whichever section it names.
  const flushPending = () => {
    if (!pending) return
    const { scope, measures } = pending
    pending = null
    const lines = measures.map(([m, a]) => mkFooterLine(scope, m, a)).filter((c) => c.length > 0)
    let owner = -1
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      if (String(stack[i].groupHeaderContent ?? '').trim().toLowerCase() === scope.toLowerCase()) {
        owner = i
        break
      }
    }
    if (owner < 0) {
      // Nothing open matches — this is the report's own closing block.
      reportFooterRows = lines
      return
    }
    // Any sections still open INSIDE the owner never got a block of their own;
    // close them unfootered so the owner's block lands on the right group.
    while (stack.length - 1 > owner) closeInnermost(null)
    closeInnermost(lines)
  }

  for (const row of rows) {
    // A continuation only extends a block that is actually open; otherwise it falls
    // through and renders verbatim as a leaf rather than being silently swallowed.
    if (pending && isSummaryContinuationRow(row, headers)) {
      pending.measures.push([text(row, measureIdx), String(row[headers[amountIdx]] ?? '')])
      continue
    }
    flushPending()
    if (isSummaryOpenerRow(row, headers)) {
      pending = {
        scope: text(row, scopeIdx),
        measures: [[text(row, measureIdx), String(row[headers[amountIdx]] ?? '')]],
      }
      continue
    }
    if (isSectionLabelRow(row, headers)) {
      openSection(text(row, 0))
      continue
    }
    const leaf = { isGroup: false, dataCells: mkCells(row) }
    leafCount += 1
    if (stack.length) inner().dataRows.push(leaf)
    else topLeaves.push(leaf)
  }
  flushPending()
  while (stack.length) closeInnermost(null)

  return {
    columns,
    reportData: [...topLeaves, ...groups],
    reportFooterRows,
    // First line stays the canonical single-row footer for existing consumers.
    reportFooterCells: reportFooterRows[0] || [],
    recordCount: leafCount,
  }
}
